/*
 * Shipping Protection toggle
 * Adds / removes a single "Shipping Protection" variant from the cart and
 * re-renders the cart drawer, cart page and cart icon using Shopify's
 * Section Rendering API — mirroring Dawn's own cart update flow.
 */
if (!customElements.get('shipping-protection')) {
  customElements.define(
    'shipping-protection',
    class ShippingProtection extends HTMLElement {
      constructor() {
        super();
        this.variantId = this.dataset.variantId;
        this.checkbox = this.querySelector('input[type="checkbox"]');
        this.busy = false;
        if (this.checkbox) {
          this.checkbox.addEventListener('change', this.onToggle.bind(this));
        }

        // "What's covered?" explanation modal
        this.modal = this.querySelector('[data-sp-modal]');
        this.onKeydown = (event) => {
          if (event.key === 'Escape') this.closeModal();
        };
        // Open trigger lives inside this element (stays in the drawer).
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-sp-open]')) {
            event.preventDefault();
            this.openModal();
          }
        });
        // Close handlers are bound to the modal itself so they keep working
        // after the modal is portaled out to <body>.
        if (this.modal) {
          this.modal.addEventListener('click', (event) => {
            if (event.target.closest('[data-sp-close]')) {
              event.preventDefault();
              this.closeModal();
            }
          });
        }
      }

      openModal() {
        if (!this.modal) return;
        // Remove any stray modals left detached by a previous cart re-render.
        document.querySelectorAll('[data-sp-modal]').forEach((m) => {
          if (m !== this.modal && !m.closest('shipping-protection')) m.remove();
        });
        // Portal to <body> (no transformed ancestor → fixed covers the full
        // viewport; high z-index sits above the drawer's z-index:1000).
        document.body.appendChild(this.modal);
        this.modal.hidden = false;
        document.addEventListener('keydown', this.onKeydown);
      }

      closeModal() {
        if (!this.modal) return;
        this.modal.hidden = true;
        document.removeEventListener('keydown', this.onKeydown);
      }

      onToggle() {
        if (this.busy) return;
        const wantOn = this.checkbox.checked;
        this.setBusy(true);

        const request = wantOn ? this.addProtection() : this.removeProtection();
        request
          .then((state) => {
            if (state && state.status) {
              // Shopify error payload (e.g. variant unavailable)
              throw new Error(state.description || state.message || 'Cart error');
            }
            this.renderSections(state);
          })
          .catch((error) => {
            console.error('[shipping-protection]', error);
            this.checkbox.checked = !wantOn; // revert UI on failure
          })
          .finally(() => this.setBusy(false));
      }

      setBusy(state) {
        this.busy = state;
        this.classList.toggle('is-loading', state);
        if (this.checkbox) this.checkbox.disabled = state;
      }

      sectionsList() {
        const list = ['cart-drawer', 'cart-icon-bubble'];
        const items = document.getElementById('main-cart-items');
        const footer = document.getElementById('main-cart-footer');
        if (items && items.dataset.id) list.push(items.dataset.id);
        if (footer && footer.dataset.id) list.push(footer.dataset.id);
        return list;
      }

      addProtection() {
        const body = JSON.stringify({
          items: [{ id: Number(this.variantId), quantity: 1 }],
          sections: this.sectionsList(),
          sections_url: window.location.pathname,
        });
        return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body }).then((r) => r.json());
      }

      removeProtection() {
        const body = JSON.stringify({
          id: String(this.variantId),
          quantity: 0,
          sections: this.sectionsList(),
          sections_url: window.location.pathname,
        });
        return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body }).then((r) => r.json());
      }

      renderSections(state) {
        if (!state || !state.sections) return;

        const apply = (targetSelector, sectionId, sourceSelector) => {
          const target = document.querySelector(targetSelector);
          const html = state.sections[sectionId];
          if (!target || html == null) return;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(sourceSelector);
          if (source) target.innerHTML = source.innerHTML;
        };

        // Cart drawer (keeps current open/closed state — we never call open())
        apply('#CartDrawer', 'cart-drawer', '#CartDrawer');
        // Header cart count bubble
        apply('#cart-icon-bubble', 'cart-icon-bubble', '.shopify-section');
        // Full cart page
        const items = document.getElementById('main-cart-items');
        const footer = document.getElementById('main-cart-footer');
        if (items) apply('#main-cart-items .js-contents', items.dataset.id, '.js-contents');
        if (footer) apply('#main-cart-footer .js-contents', footer.dataset.id, '.js-contents');

        // Let other listeners (e.g. free shipping bar) know the cart changed
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }
  );
}
